import { attachVideoSource } from './responsiveVideo';

// X7 payload configurator: a rotating drone video the user can drag-to-spin and
// that also scrubs as the section scrolls through the viewport, with a row of
// payload pills that cross-fade between pre-rendered clips.
//
// Rendered by Views/Contentful/X7PayloadConfigurator.cshtml. Styling is all
// Tailwind utilities in the markup; this file only drives behaviour. State that
// CSS needs (active video, loading, dragging) is expressed as data-* attributes
// so Tailwind data-variants can react to it. Vanilla JS — webpack entry
// "x7PayloadConfigurator".

(function () {
    'use strict';

    function init() {
        const root = document.querySelector('[data-x7pc]');
        if (!root) return;

        const stage = root.querySelector('[data-x7pc-stage]');
        const buttons = root.querySelectorAll('[data-x7pc-button]');
        const videos = stage.querySelectorAll('video');

        const videoMap = new Map();
        videos.forEach((v) => videoMap.set(v.dataset.payload, v));

        let activeVideo = null;
        let activeId = null;
        let normalizedTime = 0;
        let duration = 2.4;

        // Only the selected clip downloads, once the configurator is near the viewport.
        function ensureLoaded(video) {
            if (video.dataset.loaded === 'true') return;
            video.dataset.loaded = 'true';
            video.addEventListener('loadedmetadata', () => {
                if (isFinite(video.duration)) duration = video.duration;
                video.currentTime = normalizedTime * duration;
            });
            video.preload = 'auto';
            attachVideoSource(video);
            video.load();
        }

        // Throttle seeks to one per animation frame. Safari drops new seek requests if
        // one is already in flight, so we also wait for the seeked event before applying
        // the next pending target — guarantees the latest position lands even mid-drag.
        let pendingSeekTime = null;
        let seekFrameScheduled = false;
        function scheduleSeek() {
            if (seekFrameScheduled) return;
            seekFrameScheduled = true;
            requestAnimationFrame(applyPendingSeek);
        }
        function applyPendingSeek() {
            seekFrameScheduled = false;
            if (pendingSeekTime == null || !activeVideo) return;
            if (activeVideo.seeking) return; // a seek is in flight; the seeked handler reruns us
            const t = pendingSeekTime;
            pendingSeekTime = null;
            // Precise seek (not fastSeek): keeps frame-accurate alignment when swapping
            // between payloads.
            activeVideo.currentTime = t;
        }
        function setNormalizedTime(t) {
            normalizedTime = Math.max(0, Math.min(1, t));
            if (activeVideo && duration) {
                pendingSeekTime = normalizedTime * duration;
                scheduleSeek();
            }
        }

        videos.forEach((v) => {
            v.addEventListener('seeked', () => {
                if (v === activeVideo && pendingSeekTime != null) scheduleSeek();
            });
        });

        function selectPayload(id) {
            if (id === activeId) return;
            const next = videoMap.get(id);
            if (!next) return;

            buttons.forEach((b) => {
                const isActive = b.dataset.payload === id;
                b.classList.toggle('darkest-blue-button-filled', isActive);
                b.classList.toggle('darkest-blue-button-outline', !isActive);
            });
            activeId = id;

            ensureLoaded(next);

            const targetTime = normalizedTime * (isFinite(next.duration) ? next.duration : duration);
            if (isFinite(next.duration)) duration = next.duration;

            const doSwap = () => {
                if (activeId !== id) return; // user moved on while we waited
                next.setAttribute('data-active', '');
                if (activeVideo && activeVideo !== next) activeVideo.removeAttribute('data-active');
                activeVideo = next;
                stage.removeAttribute('data-loading');
            };

            // Need the incoming video on the correct frame *before* cross-fading,
            // otherwise the rotation appears to jump during the fade.
            const needsSeek = isFinite(next.duration) && Math.abs(next.currentTime - targetTime) > 0.01;
            const ready = next.readyState >= 2;

            if (!ready) {
                stage.setAttribute('data-loading', '');
                next.addEventListener('loadeddata', () => {
                    if (activeId !== id) return;
                    const latestTime = normalizedTime * (isFinite(next.duration) ? next.duration : duration);
                    if (Math.abs(next.currentTime - latestTime) > 0.01) {
                        next.addEventListener('seeked', doSwap, { once: true });
                        next.currentTime = latestTime;
                    } else {
                        doSwap();
                    }
                }, { once: true });
            } else if (needsSeek) {
                next.currentTime = targetTime;
                next.addEventListener('seeked', doSwap, { once: true });
            } else {
                doSwap();
            }
        }

        buttons.forEach((button) => {
            button.addEventListener('click', () => selectPayload(button.dataset.payload));
        });

        // Drag-to-spin on the stage. Once the user drags for the first time we hand
        // them control: the scroll-driven scrub below stops firing so their final
        // rotation sticks instead of being yanked away as they keep scrolling.
        let dragging = false;
        let userInteracted = false;
        let dragStartX = 0;
        let dragStartNorm = 0;
        let dragWidth = 0;
        const DRAG_SENSITIVITY = 3;

        stage.addEventListener('pointerdown', (e) => {
            if (e.target.closest('[data-x7pc-button]')) return;
            dragging = true;
            userInteracted = true;
            dragStartX = e.clientX;
            dragStartNorm = normalizedTime;
            dragWidth = stage.getBoundingClientRect().width;
            if (stage.setPointerCapture) stage.setPointerCapture(e.pointerId);
            stage.setAttribute('data-dragging', '');
        });
        stage.addEventListener('pointermove', (e) => {
            if (!dragging) return;
            const delta = ((e.clientX - dragStartX) / dragWidth) * DRAG_SENSITIVITY;
            setNormalizedTime(dragStartNorm + delta);
        });
        function endDrag() {
            dragging = false;
            stage.removeAttribute('data-dragging');
        }
        stage.addEventListener('pointerup', endDrag);
        stage.addEventListener('pointercancel', endDrag);

        // Scroll-driven scrub (no scroll-lock). As the section travels through the
        // viewport the rotation scrubs proportionally — the user just scrolls normally.
        function onScroll() {
            if (dragging) return; // don't fight an active drag
            if (userInteracted) return; // user has taken over; leave it alone
            const rect = root.getBoundingClientRect();
            const vh = window.innerHeight;
            const travel = vh + rect.height; // "just entering" → "just left"
            if (travel <= 0) return;
            const scrolledIn = vh - rect.top;
            const progress = Math.max(0, Math.min(1, scrolledIn / travel));
            setNormalizedTime(progress);
        }
        window.addEventListener('scroll', onScroll, { passive: true });

        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver(entries => {
                if (entries.some(entry => entry.isIntersecting)) {
                    if (!activeId) selectPayload(buttons[0] && buttons[0].dataset.payload);
                    observer.disconnect();
                }
            }, { rootMargin: '200px' });
            observer.observe(stage);
        } else {
            selectPayload(buttons[0] && buttons[0].dataset.payload);
        }
        onScroll();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
